/*
 * @providesModule TrackMetricPerformance
 */

import DataDogRUM from 'DataDogRUM';

type MetricCategory =
  | 'match_flow'
  | 'payment_flow'
  | 'asset_loading'
  | 'LTO'
  | 'api_call'
  | 'gameplay'
  | 'custom';

type MetricName = 'GAME_BINARY_LOAD' | 'JOIN_MATCH_FLOW' | 'CLAIM_INTERSTITIAL_PROCESS';

interface Metric {
  duration: number;
  createdAt: number;
  currentTime: number;
  category: MetricCategory;
  name: string;
  attributes?: Record<string, any>;
  finishedSteps: {step: string; duration: number; attributes?: Record<string, any>}[];
  timedOut?: boolean;
}

interface TrackMetricPerformanceOptions {
  name: MetricName;
  category: MetricCategory;
  attributes?: Record<string, any>;
  timeOut?: number;
}

interface FinishedStepOptions {
  name: MetricName;
  step: string;
  attributes?: Record<string, any>;
  metricCompleted?: boolean;
}

export default class TrackMetricPerformance {
  private static activeMetrics: Map<
    MetricName,
    {metric: Metric; timeOut?: ReturnType<typeof setTimeout>}
  > = new Map();

  static trackMetric({name, category, attributes, timeOut}: TrackMetricPerformanceOptions) {
    if (this.activeMetrics.has(name)) {
      console.warn(`Metric "${name}" is already being tracked`);
      return;
    }
    const now = Date.now();
    this.activeMetrics.set(name, {
      metric: {
        duration: 0,
        createdAt: now,
        currentTime: now,
        category,
        name,
        attributes,
        finishedSteps: [],
      },
      timeOut: !timeOut
        ? undefined
        : setTimeout(() => {
            this.completeMetric(name, true);
          }, timeOut),
    });
  }

  static addFinishedStep(options: FinishedStepOptions) {
    const {name, step, attributes, metricCompleted} = options;
    const {metric} = this.activeMetrics.get(name) || {};
    if (!metric) {
      return;
    }
    const now = Date.now();

    metric.finishedSteps.push({step, duration: now - metric.currentTime, attributes});
    metric.currentTime = now;
    if (metricCompleted) {
      this.completeMetric(name, false);
    }
  }

  static addAttributes(name: MetricName, attributes: Record<string, any>) {
    const {metric} = this.activeMetrics.get(name) || {};
    if (!metric) {
      return;
    }
    metric.attributes = {...metric.attributes, ...attributes};
  }

  static isMetricTracking(name: MetricName): boolean {
    return this.activeMetrics.has(name);
  }

  static completeMetric(name: MetricName, timedOut?: boolean) {
    const {metric, timeOut} = this.activeMetrics.get(name) || {};
    if (!metric) {
      return;
    }
    metric.duration = Date.now() - metric.createdAt;
    metric.timedOut = !!timedOut;
    DataDogRUM.addAction(name, metric);
    console.debug(`Event ${name} took`, metric.duration, metric?.finishedSteps);
    clearTimeout(timeOut);
    this.activeMetrics.delete(name);
  }
}
